export type PanelOverviewState<T> =
  | { status: "initial-loading" }
  | { status: "shell"; isRefreshing: true }
  | { status: "content"; overview: T; isRefreshing: boolean };

type DayOverviewLike = { today: unknown | null } | undefined;

/** Stale-while-revalidate resolver for Today period panels. */
export function resolvePanelOverview<T>(
  raw: T | undefined,
  cached: T | undefined,
  hasEverLoaded: boolean,
): PanelOverviewState<T> {
  if (raw !== undefined) {
    return { status: "content", overview: raw, isRefreshing: false };
  }
  if (cached !== undefined) {
    return { status: "content", overview: cached, isRefreshing: true };
  }
  if (!hasEverLoaded) {
    return { status: "initial-loading" };
  }
  return { status: "shell", isRefreshing: true };
}

type TodaySnapshot = { capturedAt?: number } | null;

function getTodaySnapshot(source: DayOverviewLike): TodaySnapshot {
  return (source as { today?: TodaySnapshot } | undefined)?.today ?? null;
}

function isPostSessionSnapshot(today: TodaySnapshot, sessionStart: number): boolean {
  if (!today || sessionStart <= 0 || typeof today.capturedAt !== "number") {
    return false;
  }
  return today.capturedAt >= sessionStart;
}

export function shouldKeepTodayLoadingShell(
  raw: DayOverviewLike,
  cached: DayOverviewLike,
  isViewingToday: boolean,
  isCollectingToday: boolean,
  todayCollectStartedAt = 0,
) {
  if (!isViewingToday) {
    return false;
  }

  const cachedToday = getTodaySnapshot(cached);
  const rawToday = getTodaySnapshot(raw);

  // Stale-while-revalidate: never trap the UI when a post-session snapshot exists.
  if (
    isPostSessionSnapshot(cachedToday, todayCollectStartedAt) ||
    isPostSessionSnapshot(rawToday, todayCollectStartedAt)
  ) {
    return false;
  }

  // Initial session only: withhold pre-launch snapshots while the first collect runs.
  if (
    isCollectingToday &&
    rawToday &&
    todayCollectStartedAt > 0 &&
    typeof rawToday.capturedAt === "number" &&
    rawToday.capturedAt < todayCollectStartedAt
  ) {
    return true;
  }

  if (!isCollectingToday || raw === undefined) {
    return false;
  }
  return rawToday == null && cachedToday == null;
}
